import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import { getApiErrorMessage } from 'src/api/axiosClient/errorParser';
import {
  useAttachDocumentsToOrderApi,
  useCheckEligibilityApi,
  useCreateHostedNumberOrderApi,
  useDeleteHostedNumberOrderApi,
  useGetAdminCompanyDocumentsApi,
  useRequestVerificationCodeApi,
  useValidateVerificationCodeApi,
} from '../api';
import {
  SetupHostedNumberStep,
  SetupHostedNumberStepStatus,
  calculateStatusAndDescription,
} from '../helpers';
import {
  SetupHostedNumberDialogProps,
  SetupHostedNumberDialogViewModel,
  VerificationMethod,
} from '../types';

export const useSetupHostedNumberDialogController = (
  params: SetupHostedNumberDialogProps,
): SetupHostedNumberDialogViewModel => {
  const { open, onClose, companyId, phoneId, phoneNumber, existingOrder } = params;

  const existingOrderStatus = existingOrder?.phoneNumbers?.[0]?.status;
  const existingOrderId = existingOrder?.orderId;
  const loaUrl = existingOrder?.documents?.loaUrl;
  const billUrl = existingOrder?.documents?.billUrl;
  const { status: orderStatus, description: orderDescription } = calculateStatusAndDescription(existingOrder, phoneNumber);
  const { openDialog: openDeleteConfirmation, ConfirmationDialog: DeleteConfirmationDialog } =
    useConfirmationDialog();
  const [activeStep, setActiveStep] = useState<SetupHostedNumberStep>(SetupHostedNumberStep.NOT_STARTED);
  const [loading, setLoading] = useState(false);

  // Step statuses and errors
  const [stepStatuses, setStepStatuses] = useState<Record<SetupHostedNumberStep, SetupHostedNumberStepStatus>>({
    [SetupHostedNumberStep.NOT_STARTED]: 'pending',
    [SetupHostedNumberStep.CHECK_ELIGIBILITY]: 'pending',
    [SetupHostedNumberStep.CREATE_ORDER]: 'pending',
    [SetupHostedNumberStep.VERIFICATION]: 'pending',
    [SetupHostedNumberStep.ATTACH_DOCUMENTS]: 'pending',
    [SetupHostedNumberStep.SUCCESS]: 'pending',
  });

  const [stepErrors, setStepErrors] = useState<Record<SetupHostedNumberStep, string | null>>({
    [SetupHostedNumberStep.NOT_STARTED]: null,
    [SetupHostedNumberStep.CHECK_ELIGIBILITY]: null,
    [SetupHostedNumberStep.CREATE_ORDER]: null,
    [SetupHostedNumberStep.VERIFICATION]: null,
    [SetupHostedNumberStep.ATTACH_DOCUMENTS]: null,
    [SetupHostedNumberStep.SUCCESS]: null,
  });

  // State for data
  const [orderId, setOrderId] = useState<string | null>(null);
  const [verificationMethod, setVerificationMethod] = useState<VerificationMethod>('sms');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationCodeSent, setVerificationCodeSent] = useState(false);

  // API Hooks
  const checkEligibility = useCheckEligibilityApi();
  const createHostedNumberOrder = useCreateHostedNumberOrderApi();
  const requestVerificationCode = useRequestVerificationCodeApi();
  const validateVerificationCode = useValidateVerificationCodeApi();
  const attachDocumentsToOrder = useAttachDocumentsToOrderApi();
  const getCompanyDocuments = useGetAdminCompanyDocumentsApi();
  const deleteHostedNumberOrder = useDeleteHostedNumberOrderApi();

  useEffect(() => {
    if (open) {
      // Reset state when dialog opens
      setLoading(false);
      setVerificationMethod('sms');
      setVerificationCode('');
      setVerificationCodeSent(false);

      setStepErrors({
        [SetupHostedNumberStep.NOT_STARTED]: null,
        [SetupHostedNumberStep.CHECK_ELIGIBILITY]: null,
        [SetupHostedNumberStep.CREATE_ORDER]: null,
        [SetupHostedNumberStep.VERIFICATION]: null,
        [SetupHostedNumberStep.ATTACH_DOCUMENTS]: null,
        [SetupHostedNumberStep.SUCCESS]: null,
      });

      // Initialize state based on existing order status
      if (existingOrderStatus === 'successful') {
        // Order is already completed
        setOrderId(existingOrderId || null);
        setActiveStep(SetupHostedNumberStep.SUCCESS);
        setStepStatuses({
          [SetupHostedNumberStep.NOT_STARTED]: 'completed',
          [SetupHostedNumberStep.CHECK_ELIGIBILITY]: 'completed',
          [SetupHostedNumberStep.CREATE_ORDER]: 'completed',
          [SetupHostedNumberStep.VERIFICATION]: 'completed',
          [SetupHostedNumberStep.ATTACH_DOCUMENTS]: 'completed',
          [SetupHostedNumberStep.SUCCESS]: 'completed',
        });

      } else if (existingOrderStatus === 'ownership_successful') {
        // Verification is done, continue from document attachment
        const orderId = existingOrderId || null;
        setOrderId(orderId);
        setActiveStep(SetupHostedNumberStep.ATTACH_DOCUMENTS);
        setStepStatuses({
          [SetupHostedNumberStep.NOT_STARTED]: 'completed',
          [SetupHostedNumberStep.CHECK_ELIGIBILITY]: 'completed',
          [SetupHostedNumberStep.CREATE_ORDER]: 'completed',
          [SetupHostedNumberStep.VERIFICATION]: 'completed',
          [SetupHostedNumberStep.ATTACH_DOCUMENTS]: loaUrl != null && billUrl != null ? 'completed' : 'in-progress',
          [SetupHostedNumberStep.SUCCESS]: loaUrl != null && billUrl != null ? 'completed' : 'pending',
        });
      } else if (existingOrderStatus === 'pending') {
        // Eligibility checked and order created, continue from verification
        setOrderId(existingOrderId || null);
        setActiveStep(SetupHostedNumberStep.VERIFICATION);
        setStepStatuses({
          [SetupHostedNumberStep.NOT_STARTED]: 'completed',
          [SetupHostedNumberStep.CHECK_ELIGIBILITY]: 'completed',
          [SetupHostedNumberStep.CREATE_ORDER]: 'completed',
          [SetupHostedNumberStep.VERIFICATION]: 'pending',
          [SetupHostedNumberStep.ATTACH_DOCUMENTS]: 'pending',
          [SetupHostedNumberStep.SUCCESS]: 'pending',
        });
      } else {
        // No existing order, start from beginning
        setOrderId(null);
        setActiveStep(SetupHostedNumberStep.NOT_STARTED);
        setStepStatuses({
          [SetupHostedNumberStep.NOT_STARTED]: 'pending',
          [SetupHostedNumberStep.CHECK_ELIGIBILITY]: 'pending',
          [SetupHostedNumberStep.CREATE_ORDER]: 'pending',
          [SetupHostedNumberStep.VERIFICATION]: 'pending',
          [SetupHostedNumberStep.ATTACH_DOCUMENTS]: 'pending',
          [SetupHostedNumberStep.SUCCESS]: 'pending',
        });
      }
    }
  }, [open, existingOrderStatus, existingOrderId]);

  const handleCreateOrder = async () => {
    setLoading(true);
    setStepStatuses(prev => ({ ...prev, [SetupHostedNumberStep.CREATE_ORDER]: 'in-progress' }));
    setStepErrors(prev => ({ ...prev, [SetupHostedNumberStep.CREATE_ORDER]: null }));
    try {
      const response = await createHostedNumberOrder(companyId, phoneId);
      setOrderId(response.data.orderId);
      setStepStatuses(prev => ({ ...prev, [SetupHostedNumberStep.CREATE_ORDER]: 'completed' }));
      setActiveStep(SetupHostedNumberStep.VERIFICATION);
    } catch (err: unknown) {
      setStepStatuses(prev => ({ ...prev, [SetupHostedNumberStep.CREATE_ORDER]: 'error' }));
      const errorMsg = getApiErrorMessage(err, 'Failed to create order');
      setStepErrors(prev => ({ ...prev, [SetupHostedNumberStep.CREATE_ORDER]: errorMsg }));
    } finally {
      setLoading(false);
    }
  };

  const handleCheckEligibility = async () => {
    setLoading(true);
    setStepStatuses(prev => ({ ...prev, [SetupHostedNumberStep.CHECK_ELIGIBILITY]: 'in-progress' }));
    setStepErrors(prev => ({ ...prev, [SetupHostedNumberStep.CHECK_ELIGIBILITY]: null }));
    try {
      const response = await checkEligibility(phoneNumber);
      if (response.data.isEligible) {
        setStepStatuses(prev => ({ ...prev, [SetupHostedNumberStep.CHECK_ELIGIBILITY]: 'completed' }));
        setActiveStep(SetupHostedNumberStep.CREATE_ORDER);
        // Auto-proceed to create order
        handleCreateOrder();
      } else {
        setStepStatuses(prev => ({ ...prev, [SetupHostedNumberStep.CHECK_ELIGIBILITY]: 'error' }));
        const errorMsg = response.data.detail || 'Phone number is not eligible for 10DLC hosting';
        setStepErrors(prev => ({ ...prev, [SetupHostedNumberStep.CHECK_ELIGIBILITY]: errorMsg }));
      }
    } catch (err: unknown) {
      setStepStatuses(prev => ({ ...prev, [SetupHostedNumberStep.CHECK_ELIGIBILITY]: 'error' }));
      const errorMsg = getApiErrorMessage(err, 'Failed to check eligibility');
      setStepErrors(prev => ({ ...prev, [SetupHostedNumberStep.CHECK_ELIGIBILITY]: errorMsg }));
    } finally {
      setLoading(false);
    }
  };

  const onStartProcess = () => {
    setActiveStep(SetupHostedNumberStep.CHECK_ELIGIBILITY);
    handleCheckEligibility();
  };

  const onRestartVerification = () => {
    setVerificationCodeSent(false);
    setStepStatuses(prev => ({ ...prev, [SetupHostedNumberStep.VERIFICATION]: 'pending' }));
    setStepErrors(prev => ({ ...prev, [SetupHostedNumberStep.VERIFICATION]: null }));
    setVerificationCode('');
  };

  const onRequestVerificationCode = async () => {
    if (!orderId) return;
    setLoading(true);
    setStepStatuses(prev => ({ ...prev, [SetupHostedNumberStep.VERIFICATION]: 'in-progress' }));
    setStepErrors(prev => ({ ...prev, [SetupHostedNumberStep.VERIFICATION]: null }));
    try {
      await requestVerificationCode(companyId, phoneId, orderId, verificationMethod);
      setVerificationCodeSent(true);
    } catch (err: unknown) {
      setStepStatuses(prev => ({ ...prev, [SetupHostedNumberStep.VERIFICATION]: 'error' }));
      const errorMsg = getApiErrorMessage(err, 'Failed to request verification code');
      setStepErrors(prev => ({ ...prev, [SetupHostedNumberStep.VERIFICATION]: errorMsg }));
    } finally {
      setLoading(false);
    }
  };

  const onAttachDocuments = async () => {
    if (!orderId) return;
    setLoading(true);
    setStepStatuses(prev => ({ ...prev, [SetupHostedNumberStep.ATTACH_DOCUMENTS]: 'in-progress' }));
    setStepErrors(prev => ({ ...prev, [SetupHostedNumberStep.ATTACH_DOCUMENTS]: null }));
    try {
      // 1. Fetch documents to find latest LOA and Phone Bill
      const documentsResponse = await getCompanyDocuments(companyId, {
        limit: 100,
        sort: 'date_created_desc',
      });
      const documents = documentsResponse.data.items;

      // Let's refine the search.
      const loa = documents.find(d => d.documentType.toLowerCase().includes('loa'));
      const phoneBill = documents.find(d => d.documentType.toLowerCase().includes('bill'));

      if (!loa || !phoneBill) {
        setStepStatuses(prev => ({ ...prev, [SetupHostedNumberStep.ATTACH_DOCUMENTS]: 'error' }));
        const errorMsg = 'Please attach LOA and the latest Phone Bill documents to company';
        setStepErrors(prev => ({ ...prev, [SetupHostedNumberStep.ATTACH_DOCUMENTS]: errorMsg }));
        return;
      }

      await attachDocumentsToOrder(companyId, phoneId, orderId, loa.id, phoneBill.id);
      setStepStatuses(prev => ({ ...prev, [SetupHostedNumberStep.ATTACH_DOCUMENTS]: 'completed' }));
      setActiveStep(SetupHostedNumberStep.SUCCESS);
      setStepStatuses(prev => ({ ...prev, [SetupHostedNumberStep.SUCCESS]: 'completed' }));
      toast.success('Phone number setup completed successfully!');
    } catch (err: unknown) {
      console.error(err);
      setStepStatuses(prev => ({ ...prev, [SetupHostedNumberStep.ATTACH_DOCUMENTS]: 'error' }));
      const errorMsg = getApiErrorMessage(err, 'Failed to attach documents');
      setStepErrors(prev => ({ ...prev, [SetupHostedNumberStep.ATTACH_DOCUMENTS]: errorMsg }));
    } finally {
      setLoading(false);
    }
  };

  const onValidateVerificationCode = async () => {
    if (!orderId) return;
    setLoading(true);
    setStepStatuses(prev => ({ ...prev, [SetupHostedNumberStep.VERIFICATION]: 'in-progress' }));
    setStepErrors(prev => ({ ...prev, [SetupHostedNumberStep.VERIFICATION]: null }));
    try {
      const response = await validateVerificationCode(
        companyId,
        phoneId,
        orderId,
        verificationCode,
      );
      if (response.data.status === 'verified' || response.data.status === 'already_verified') {
        setStepStatuses(prev => ({ ...prev, [SetupHostedNumberStep.VERIFICATION]: 'completed' }));
        setStepErrors(prev => ({ ...prev, [SetupHostedNumberStep.VERIFICATION]: null }));
        setActiveStep(SetupHostedNumberStep.ATTACH_DOCUMENTS);
        // Auto-proceed to attach documents
        onAttachDocuments();
      } else {
        setStepStatuses(prev => ({ ...prev, [SetupHostedNumberStep.VERIFICATION]: 'error' }));
        setStepErrors(prev => ({ ...prev, [SetupHostedNumberStep.VERIFICATION]: 'Invalid verification code' }));
      }
    } catch (err: unknown) {
      setStepStatuses(prev => ({ ...prev, [SetupHostedNumberStep.VERIFICATION]: 'error' }));
      const errorMsg = getApiErrorMessage(err, 'Failed to validate verification code');
      setStepErrors(prev => ({ ...prev, [SetupHostedNumberStep.VERIFICATION]: errorMsg }));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrder = async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      await deleteHostedNumberOrder(companyId, orderId);
      onClose();
    } catch (err: unknown) {
      console.error(getApiErrorMessage(err, 'Failed to delete order'));
      toast.error(`Failed to delete order.`);
    } finally {
      setLoading(false);
    }
  };

  const onRequestDeleteOrder = () => {
    const otherPhoneNumbers =
      existingOrder?.phoneNumbers?.filter(p => p.phoneNumber !== phoneNumber) ?? [];
    const mentionAnotherNumbers =
      otherPhoneNumbers.length > 0
        ? ` This will also affect the following phone numbers: ${otherPhoneNumbers
            .map(p => p.phoneNumber)
            .join(', ')}. `
        : '';

    openDeleteConfirmation({
      title: 'Delete Order',
      content: `Are you sure you want to delete the order for phone number ${phoneNumber}? ${mentionAnotherNumbers}The action cannot be undone.`,
      onConfirm: handleDeleteOrder,
      confirmButtonText: 'Delete',
      confirmButtonColor: 'error',
    });
  };

  const dialogTitle =
    existingOrderStatus === 'successful'
      ? `Phone Number ${phoneNumber} - Setup Complete`
      : `Setup Phone Number ${phoneNumber}`;

  return {
    open,
    onClose,
    dialogTitle,
    isExistingOrderSuccessful: existingOrderStatus === 'successful',
    isOwnershipSuccessfulOrder: existingOrderStatus === 'ownership_successful',
    hasExistingOrderDocuments: existingOrder?.documents != null,
    loaUrl,
    billUrl,
    orderStatus,
    orderDescription,
    activeStep,
    loading,
    stepStatuses,
    stepErrors,
    orderId,
    verificationMethod,
    verificationCode,
    verificationCodeSent,
    setVerificationMethod,
    setVerificationCode,
    DeleteConfirmationDialog,
    onStartProcess,
    onRequestVerificationCode,
    onValidateVerificationCode,
    onRestartVerification,
    onAttachDocuments,
    onRequestDeleteOrder,
  };
};
